import type {
  LiveCallInfo,
  LiveCallResponse,
  LiveCallStep,
  LiveCallStatus,
  LiveCallTurn,
} from '@/services/conversationService';

/**
 * Pure state of the live phone view (livecenter contract §3): finished lines
 * (`call.turn`), growing partial lines (`call.partial`, replaced by the turn
 * with the same `seq`), quiet tool steps (`call.step`) and the call status
 * (`call.live`). No React, no I/O; assignment only by `call_sid`.
 */

/** `call.live` payload. */
export interface CallLiveEvent {
  call_sid: string;
  status: 'started' | 'transferring' | 'ended' | string;
  customer_phone: string;
  customer_name: string | null;
  agent_number: string | null;
  started_at: string | null;
  ended_at: string | null;
  conversation_id: number | null;
}

/** `call.turn` payload: a finished line. */
export interface CallTurnEvent extends LiveCallTurn {
  call_sid: string;
}

/** `call.partial` payload (ephemeral): the line at `seq` so far. */
export interface CallPartialEvent {
  call_sid: string;
  seq: number;
  role: 'user' | 'assistant';
  content: string;
}

/** `call.step` payload. */
export interface CallStepEvent extends LiveCallStep {
  call_sid: string;
}

export interface CallLine extends LiveCallTurn {
  /** Still being spoken (from `call.partial`). */
  partial: boolean;
  /**
   * Where the line sits in time: when it was first seen. A finished turn is
   * stamped at its end, which would push a line below the tool step it
   * triggered; keeping the first-seen time holds it in place.
   */
  orderAt: number;
}

export interface LiveCallState {
  call: LiveCallInfo;
  /** Sorted by `seq`. */
  lines: CallLine[];
  /** Sorted by time. */
  steps: LiveCallStep[];
}

export type CallTimelineEntry =
  | { type: 'turn'; key: string; turn: CallLine }
  | {
      type: 'step';
      key: string;
      step: LiveCallStep;
      /** `call.step` arrives when a tool STARTS: it runs until the next step
       *  or line supersedes it (and never after the call ended). */
      active: boolean;
    };

function parse(ts: string | null | undefined): number | null {
  if (!ts) return null;
  const t = Date.parse(ts);
  return Number.isNaN(t) ? null : t;
}

function lineFromTurn(turn: LiveCallTurn, fallbackAt: number): CallLine {
  return { ...turn, partial: false, orderAt: parse(turn.ts) ?? fallbackAt };
}

function bySeq(a: CallLine, b: CallLine): number {
  return a.seq - b.seq;
}

function byTime(a: LiveCallStep, b: LiveCallStep): number {
  return (parse(a.ts) ?? Number.MAX_SAFE_INTEGER) - (parse(b.ts) ?? Number.MAX_SAFE_INTEGER);
}

function sameStep(a: LiveCallStep, b: LiveCallStep): boolean {
  return a.label === b.label && (a.tool ?? null) === (b.tool ?? null) && parse(a.ts) === parse(b.ts);
}

export function createLiveCallState(res: LiveCallResponse, now: number = Date.now()): LiveCallState {
  const lines = res.turns.map((t) => lineFromTurn(t, now)).sort(bySeq);
  const steps = [...res.steps].sort(byTime);
  const lastStep = steps.length ? steps[steps.length - 1].label : null;
  return { call: { ...res.call, last_step: res.call.last_step ?? lastStep }, lines, steps };
}

export function applyCallTurn(state: LiveCallState, event: CallTurnEvent): LiveCallState {
  if (event.call_sid !== state.call.call_sid) return state;
  const existing = state.lines.find((l) => l.seq === event.seq);
  if (existing && !existing.partial && existing.content === event.content && existing.ts === event.ts) return state;
  const at = parse(event.ts) ?? Date.now();
  const line: CallLine = {
    seq: event.seq,
    role: event.role,
    content: event.content,
    ts: event.ts,
    partial: false,
    orderAt: existing ? Math.min(existing.orderAt, at) : at,
  };
  const lines = [...state.lines.filter((l) => l.seq !== event.seq), line].sort(bySeq);
  return { ...state, lines };
}

/** `now` is the server-clock time the partial arrived (it carries no time itself). */
export function applyCallPartial(state: LiveCallState, event: CallPartialEvent, now: number): LiveCallState {
  if (event.call_sid !== state.call.call_sid) return state;
  const existing = state.lines.find((l) => l.seq === event.seq);
  // The finished line is final; a partial that arrives late must not undo it.
  if (existing && !existing.partial) return state;
  if (existing && existing.content === event.content) return state;
  const line: CallLine = {
    seq: event.seq,
    role: event.role,
    content: event.content,
    ts: null,
    partial: true,
    orderAt: existing ? existing.orderAt : now,
  };
  const lines = [...state.lines.filter((l) => l.seq !== event.seq), line].sort(bySeq);
  return { ...state, lines };
}

export function applyCallStep(state: LiveCallState, event: CallStepEvent): LiveCallState {
  if (event.call_sid !== state.call.call_sid) return state;
  const step: LiveCallStep = { label: event.label, tool: event.tool ?? null, ts: event.ts ?? null };
  if (state.steps.some((s) => sameStep(s, step))) return state;
  const steps = [...state.steps, step].sort(byTime);
  return { ...state, steps, call: { ...state.call, last_step: step.label } };
}

function statusOf(raw: string, fallback: LiveCallStatus): LiveCallStatus {
  if (raw === 'started' || raw === 'live') return 'live';
  if (raw === 'transferring' || raw === 'ended') return raw;
  return fallback;
}

/**
 * `call.live`: `transferring` during a transfer; a transfer that comes back
 * sends `started` again with the same sid — the same view goes on. On
 * `ended`, partial lines that never got their finished turn are dropped.
 */
export function applyCallLive(state: LiveCallState, event: CallLiveEvent): LiveCallState {
  if (event.call_sid !== state.call.call_sid) return state;
  const call: LiveCallInfo = {
    ...state.call,
    status: statusOf(event.status, state.call.status),
    customer_phone: event.customer_phone || state.call.customer_phone,
    customer_name: event.customer_name?.trim() ? event.customer_name : state.call.customer_name,
    agent_number: event.agent_number ?? state.call.agent_number,
    started_at: event.started_at ?? state.call.started_at,
    ended_at: event.ended_at ?? state.call.ended_at ?? null,
    conversation_id: event.conversation_id ?? state.call.conversation_id ?? null,
  };
  const lines = call.status === 'ended' && state.lines.some((l) => l.partial)
    ? state.lines.filter((l) => !l.partial)
    : state.lines;
  return { ...state, call, lines };
}

/**
 * A refetch (reconnect, degraded polling) is the server's truth for finished
 * lines, steps and status. Partial lines are ephemeral and never stored, so
 * the ones beyond the server's last turn stay.
 */
export function mergeCallRefresh(state: LiveCallState, res: LiveCallResponse, now: number): LiveCallState {
  if (res.call.call_sid !== state.call.call_sid) return createLiveCallState(res, now);
  const fresh = createLiveCallState(res, now);
  const finished = new Set(fresh.lines.map((l) => l.seq));
  const partials = state.lines.filter((l) => l.partial && !finished.has(l.seq));
  const lines = [
    ...fresh.lines.map((l) => {
      const known = state.lines.find((s) => s.seq === l.seq);
      return known ? { ...l, orderAt: Math.min(known.orderAt, l.orderAt) } : l;
    }),
    ...partials,
  ].sort(bySeq);
  return { ...fresh, lines };
}

/**
 * Lines in `seq` order (gaps and jumps allowed) with the tool steps merged in
 * by time. Each list keeps its own order; a step goes before the first line
 * that started after it. Only the last entry can be a running step.
 */
export function callTimeline(state: LiveCallState): CallTimelineEntry[] {
  const entries: CallTimelineEntry[] = [];
  let s = 0;
  const pushStep = (index: number) =>
    entries.push({ type: 'step', key: `step-${index}`, step: state.steps[index], active: false });
  state.lines.forEach((line) => {
    while (s < state.steps.length) {
      const stepAt = parse(state.steps[s].ts);
      if (stepAt === null || stepAt >= line.orderAt) break;
      pushStep(s);
      s += 1;
    }
    entries.push({ type: 'turn', key: `turn-${line.seq}`, turn: line });
  });
  for (; s < state.steps.length; s += 1) pushStep(s);

  const last = entries[entries.length - 1];
  if (last?.type === 'step' && state.call.status !== 'ended') {
    entries[entries.length - 1] = { ...last, active: true };
  }
  return entries;
}
